import cn from 'classnames'
import { createElement, memo, ReactNode, useCallback, useMemo, useState } from 'react'

import { LoaderOverlay } from 'cng-common/components/loaderOverlay'
import { Translation } from 'cng-common/features/translations/components/translation'
import { Button } from 'core-components/library/button'
import { TStepperStep } from 'shared/components/stepper/models'
import { Stepper } from 'shared/components/stepper/Stepper'

import { canNotProceed, getStepperConfig } from './helpers'
import { IWizardProps, TWizardStep } from './models'
import { wizardTranslation } from './translation'

import styles from './Wizard.module.scss'

const welcomeStep: TWizardStep = {
  id: 'welcome',
  title: wizardTranslation.welcome.title,
  description: wizardTranslation.welcome.description,
  // @ts-ignore
  content: () => wizardTranslation.welcome.content,
}

export const Wizard = memo<IWizardProps>(
  ({
    hasStepper,
    onCancel,
    steps,
    startStep = 0,
    stepperSize,
    shared,
    cancelButtonContent = wizardTranslation.navigation.cancel,
    isFreeMode,
    classes,
  }) => {
    const [currentStepIndex, setCurrentStepIndex] = useState(startStep)
    const [isLoading, setIsLoading] = useState(false)

    const isFirstStep = useMemo(() => currentStepIndex === 0, [currentStepIndex])

    const currentStep = useMemo(
      () => steps[currentStepIndex] || welcomeStep,
      [currentStepIndex, steps]
    )

    const {
      completeButtonContent = wizardTranslation.navigation.complete,
      backButtonContent = wizardTranslation.navigation.back,
      content,
      title,
      description,
      onStepChange = () => ({ hasError: false, changeStep: true }),
      isBackStepDisabled,
      isNextStepDisabled,
      isCompleteButtonDisabled,
      stepChangeMessage,
      showSaveAsDraft,
      saveAsDraftButtonContent,
      onSaveAsDraft,
    } = useMemo(() => currentStep, [currentStep])

    const stepperConfig = useMemo<TStepperStep[]>(() => {
      if (hasStepper) {
        return getStepperConfig({ steps, currentStepIndex, isFreeMode })
      }

      return []
    }, [currentStepIndex, hasStepper, isFreeMode, steps])

    const goToStep = useCallback(
      (nextStepIndex: number) => {
        if (isLoading) {
          return
        }

        setIsLoading(true)
        Promise.resolve(onStepChange(currentStepIndex, nextStepIndex))
          .then((result) => {
            if (canNotProceed(result)) {
              return
            }
            setCurrentStepIndex(nextStepIndex)
          })
          .finally(() => {
            setIsLoading(false)
          })
      },
      [currentStepIndex, isLoading, onStepChange]
    )

    const renderButtonContent = useCallback((content: ReactNode) => {
      if (typeof content === 'string') {
        return <Translation text={content} span htmlAvailable />
      }
      return content
    }, [])

    const handleOnStepCompleted = useCallback(() => {
      if (isLoading) {
        return
      }

      goToStep(currentStepIndex + 1)
    }, [currentStepIndex, goToStep, isLoading])

    const handleOnStepBack = useCallback(() => {
      if (isLoading) {
        return
      }

      goToStep(currentStepIndex - 1)
    }, [currentStepIndex, goToStep, isLoading])

    const handleOnStepChange = useCallback(
      (nextStepIndex: number) => {
        if ((nextStepIndex < currentStepIndex || isFreeMode) && !isBackStepDisabled) {
          goToStep(nextStepIndex)
        }
      },
      [currentStepIndex, goToStep, isBackStepDisabled, isFreeMode]
    )

    const handleOnCancel = useCallback(
      (...args: unknown[]) => {
        if (isLoading) {
          return
        }

        if (onCancel) {
          onCancel(...args)
        }
      },
      [isLoading, onCancel]
    )

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter') {
        event.stopPropagation()
        event.preventDefault()
        if (isLoading || isNextStepDisabled || isCompleteButtonDisabled) {
          return
        }
        handleOnStepCompleted()
      }
    }

    const handleOnSaveAsDraft = useCallback(() => {
      if (!onSaveAsDraft || isLoading) {
        return
      }

      setIsLoading(true)
      Promise.resolve(onSaveAsDraft()).finally(() => {
        setIsLoading(false)
      })
    }, [isLoading, onSaveAsDraft])

    const renderContent = useCallback(
      () =>
        createElement(content, {
          onStepChange: handleOnStepChange,
          onStepCompleted: handleOnStepCompleted,
          onStepBack: handleOnStepBack,
          onCancel: handleOnCancel,
        }),
      [content, handleOnCancel, handleOnStepBack, handleOnStepChange, handleOnStepCompleted]
    )

    return (
      <div className={cn(styles.container, classes?.container)}>
        {/* Header section */}
        <div className={cn(styles.header, classes?.header)}>
          <div className={styles.titleContainer}>
            <h2 className={styles.title}>
              <Translation text={title} span />
            </h2>
            <p className={cn(styles.description, { [styles.hidden]: !description })}>
              <Translation text={description ?? ''} span />
            </p>
          </div>
          <div className={cn(styles.stepper, { [styles.hidden]: !hasStepper })}>
            <Stepper
              steps={stepperConfig}
              onClick={handleOnStepChange}
              color="white"
              variant={stepperSize}
            />
          </div>
        </div>
        {shared}
        {/* Content section */}
        <div className={cn(styles.content, classes?.content)} onKeyDown={handleKeyDown}>
          {renderContent()}
        </div>
        {/* Footer section */}
        <div className={cn(styles.footer, classes?.footer)}>
          <Button
            color="danger"
            className={cn({ [styles.hidden]: !onCancel })}
            disabled={isLoading}
            onClick={handleOnCancel}
          >
            {renderButtonContent(cancelButtonContent)}
          </Button>
          <Button
            className={cn({ [styles.hidden]: isFirstStep })}
            disabled={isLoading || isFirstStep || isBackStepDisabled}
            onClick={handleOnStepBack}
          >
            {renderButtonContent(backButtonContent)}
          </Button>
          {/* Button for onboarding */}
          {showSaveAsDraft && onSaveAsDraft && (
            <Button disabled={isLoading || isNextStepDisabled} onClick={handleOnSaveAsDraft}>
              {renderButtonContent(saveAsDraftButtonContent ?? 'Save as Draft')}
            </Button>
          )}
          <Button
            disabled={isLoading || isNextStepDisabled || isCompleteButtonDisabled}
            onClick={handleOnStepCompleted}
          >
            {renderButtonContent(completeButtonContent)}
          </Button>
        </div>
        {/* Loader */}
        <LoaderOverlay isVisible={isLoading} message={stepChangeMessage} />
      </div>
    )
  }
)

Wizard.displayName = 'Wizard'
